import { useEffect, useState } from 'react';
import { api } from '../api';
import { useAuth } from '../auth';
import { ErrorBox } from '../ui';

type TgWebApp = {
  initData: string;
  ready: () => void;
  expand: () => void;
};

/**
 * Вход в кабинет — самое дорогое место в системе, если сделать его наивно.
 *
 * Порядок попыток, от бесплатного к платному:
 *   1. Открыты внутри Telegram/MAX → мессенджер уже подтвердил личность,
 *      входим по подписи initData. Ни одной SMS.
 *   2. Токен в памяти браузера живёт 90 дней — повторный вход бесплатен.
 *   3. Только если ничего не подошло — код на телефон. И то не чаще раза
 *      в минуту: сервер не выпустит второй код, пока жив первый.
 */
export function ClientLogin() {
  const { login } = useAuth();
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [sent, setSent] = useState<{ channel?: string; devCode?: string; retryAfterSec?: number } | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const [miniapp, setMiniapp] = useState<'telegram' | 'max' | null>(null);

  useEffect(() => {
    const w = window as unknown as { Telegram?: { WebApp?: TgWebApp }; MAX?: { WebApp?: TgWebApp } };
    const tg = w.Telegram?.WebApp;
    const max = w.MAX?.WebApp;
    const platform = tg?.initData ? 'telegram' : max?.initData ? 'max' : null;
    if (!platform) return;
    setMiniapp(platform);
    const initData = (platform === 'telegram' ? tg : max)!.initData;
    (platform === 'telegram' ? tg : max)!.ready?.();
    void api<{ token: string; scope: string }>('/auth/miniapp', { body: { platform, initData } })
      .then((r) => login(r.token, r.scope))
      .catch(() => setMiniapp(null)); // подпись не сошлась — обычный вход по коду
  }, [login]);

  if (miniapp) {
    return <div className="mobile"><div className="body">Входим через {miniapp}…</div></div>;
  }

  return (
    <div className="login">
      <img className="logo" src="/logo.png" alt="Логотип компании" />
      <div className="card">
        <h2>Вход в личный кабинет</h2>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            setBusy(true);
            setError(null);
            if (!sent) {
              void api<{ channel: string; devCode?: string; retryAfterSec?: number }>('/auth/otp/request', {
                body: { phone },
              })
                .then(setSent)
                .catch(setError)
                .finally(() => setBusy(false));
            } else {
              // scope: 'client' — иначе водитель, который заказывает воду себе
              // домой, попадёт в рабочее место вместо кабинета
              void api<{ token: string; scope: string }>('/auth/otp/verify', {
                body: { phone, code, scope: 'client' },
              })
                .then((r) => login(r.token, r.scope))
                .catch(setError)
                .finally(() => setBusy(false));
            }
          }}
        >
          <div className="field">
            <label>Номер телефона</label>
            <input
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="+7 ___ ___-__-__"
              inputMode="tel"
              autoFocus
            />
          </div>
          {sent ? (
            <div className="field">
              <label>Код из сообщения</label>
              <input value={code} onChange={(e) => setCode(e.target.value)} inputMode="numeric" autoFocus />
              {sent.devCode ? (
                <div className="ok">Стенд: код {sent.devCode}</div>
              ) : sent.retryAfterSec ? (
                // Сервер не выпустил второй код: прошлый ещё жив. Это не ошибка,
                // а экономия — говорим об этом прямо, чтобы человек не жал ещё раз
                <div className="muted">
                  Код уже отправлен, введите его. Новый можно запросить через {sent.retryAfterSec} сек.
                </div>
              ) : (
                <div className="muted">Отправили в {sent.channel ?? 'SMS'}. Код живёт 5 минут.</div>
              )}
            </div>
          ) : null}
          <button className="primary big" style={{ width: '100%' }} disabled={busy}>
            {sent ? 'Войти' : 'Получить код'}
          </button>
        </form>
        <ErrorBox error={error} />
        <div className="muted" style={{ fontSize: 12, marginTop: 16 }}>
          Код спрашиваем один раз: дальше вход сохраняется на три месяца.
          В Telegram и MAX кабинет открывается вообще без кода.
        </div>
      </div>
    </div>
  );
}
